import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  Divider,
  Button,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowBack as BackIcon } from "@mui/icons-material";
import { api } from "../../services/api";
import { formatDate } from "../../utils/dateFormat";
import PublicLayout from "./PublicLayout";
import { HOMEPAGE_CACHE_KEY } from "../../config/constants";

// Helper function to get video embed URL
const getVideoEmbed = (url: string): { type: string; embedUrl: string } | null => {
  try {
    const urlObj = new URL(url);
    const hostname = urlObj.hostname.toLowerCase();

    // YouTube
    if (hostname.includes('youtube.com') || hostname.includes('youtu.be')) {
      let videoId = '';
      if (hostname.includes('youtu.be')) {
        videoId = urlObj.pathname.slice(1).split('/')[0];
      } else if (urlObj.pathname.includes('/embed/')) {
        videoId = urlObj.pathname.split('/embed/')[1].split('/')[0];
      } else if (urlObj.pathname.includes('/shorts/')) {
        videoId = urlObj.pathname.split('/shorts/')[1].split('/')[0];
      } else {
        videoId = urlObj.searchParams.get('v') || '';
      }
      if (videoId) {
        return { type: 'youtube', embedUrl: `https://www.youtube.com/embed/${videoId}` };
      }
    }

    // Instagram
    if (hostname.includes('instagram.com')) {
      const match = url.match(/instagram\.com\/(p|reel|reels|tv)\/([^/?]+)/);
      if (match) {
        return { type: 'instagram', embedUrl: `https://www.instagram.com/p/${match[2]}/embed` };
      }
    }

    // Facebook
    if (hostname.includes('facebook.com') || hostname.includes('fb.watch') || hostname.includes('fb.com')) {
      return { type: 'facebook', embedUrl: `https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(url)}&show_text=false&width=560` };
    }

    return null;
  } catch {
    return null;
  }
};

// Video embed component
const VideoEmbed: React.FC<{ url: string }> = ({ url }) => {
  const embed = getVideoEmbed(url);

  if (!embed) {
    return (
      <Box sx={{ mb: 2 }}>
        <a href={url} target="_blank" rel="noopener noreferrer" style={{ color: "#1976d2" }}>
          {url}
        </a>
      </Box>
    );
  }

  // Instagram videos need more height (min 700px) to show full content including play button
  // YouTube and Facebook use standard 16:9 aspect ratio
  const isInstagram = embed.type === 'instagram';

  return (
    <Box sx={{
      mb: 3,
      position: 'relative',
      paddingTop: isInstagram ? '0' : '56.25%',
      minHeight: isInstagram ? '700px' : 'auto',
      width: '100%'
    }}>
      <iframe
        src={embed.embedUrl}
        style={{
          position: isInstagram ? 'relative' : 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: isInstagram ? '700px' : '100%',
          border: 'none',
        }}
        allowFullScreen
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        title={`${embed.type} video`}
      />
    </Box>
  );
};

interface NewsArticle {
  id: string;
  title: string;
  subtitle: string;
  content: string;
  images?: string[];
  videoLinks?: string[];
  publishedAt: any;
  category?: string;
}

const ArticleDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [article, setArticle] = useState<NewsArticle | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [articleETag, setArticleETag] = useState<string | null>(null);
  
  // Generate random decorative bullets once on mount
  const [decorativeBullets] = useState(() => {
    const rainbowColors = [
      '#FF6B6B', // Red
      '#FF8C42', // Orange
      '#FFD93D', // Yellow
      '#6BCF7F', // Green
      '#4D96FF', // Blue
      '#9D84FF', // Indigo
      '#C77DFF', // Violet
      '#FF6B9D', // Pink
    ];
    
    const flowers = ['✿', '❀', '❁', '✾', '✽', '❃', '❊', '❋'];
    
    return Array.from({ length: 5 }, () => {
      const randomColorIndex = Math.floor(Math.random() * rainbowColors.length);
      const randomFlowerIndex = Math.floor(Math.random() * flowers.length);
      
      return {
        color: rainbowColors[randomColorIndex],
        symbol: flowers[randomFlowerIndex],
      };
    });
  });

  useEffect(() => {
    const fetchArticle = async () => {
      if (!id) return;

      try {
        setLoading(true);

        // STEP 1: Try to find article in cached snapshot (instant load!)
        try {
          const cached = sessionStorage.getItem(HOMEPAGE_CACHE_KEY);
          if (cached) {
            const { data } = JSON.parse(cached);
            // Search for article in cached data (data.articles is the array)
            const foundArticle = data.articles?.find((a: any) => a.id === id);

            if (foundArticle) {
              console.log("Article found in snapshot cache (instant load!)");
              const articleData = foundArticle as NewsArticle;
              setArticle(articleData);
              document.title = `${articleData.title} - News Portal`;
              setLoading(false);
              return; // Done! No API call needed
            }
          }
        } catch (cacheError) {
          console.log("Cache check failed, falling back to API");
        }

        // STEP 2: Article not in snapshot - fallback to getPublishedArticle
        console.log("Article not in snapshot, fetching from API (fallback)");
        const result = await api.getPublishedArticle(id, articleETag || undefined);

        if (result.success) {
          // If not modified, article stays the same (304 response)
          if (!result.modified) {
            console.log("Article not modified, using cached version");
            setLoading(false);
            return;
          }

          if (result.article) {
            const articleData = result.article as NewsArticle;
            setArticle(articleData);

            // Store ETag for future requests
            if (result.etag) {
              setArticleETag(result.etag);
            }

            // Set document title for sharing
            document.title = `${articleData.title} - News Portal`;
          } else {
            setError("Article not found");
          }
        } else {
          // Handle rate limiting gracefully
          if (result.retryAfter) {
            setError(
              `Too many requests. Please wait ${result.retryAfter} seconds and try again.`
            );
          } else {
            setError(result.error || "Failed to load article");
          }
        }
      } catch (err: any) {
        setError(err.message || "Failed to load article");
      } finally {
        setLoading(false);
      }
    };

    fetchArticle();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleBack = () => {
    navigate("/");
  };

  if (loading) {
    return (
      <PublicLayout hideLastUpdated={true}>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
        >
          <CircularProgress />
        </Box>
      </PublicLayout>
    );
  }

  if (error || !article) {
    return (
      <PublicLayout hideLastUpdated={true}>
        <Alert severity="error">{error || "Article not found"}</Alert>
        <Button
          startIcon={<BackIcon />}
          onClick={handleBack}
          sx={{ mt: 2, py: 2 }}
        >
          Back to Homepage
        </Button>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout maxWidth="md" hideLastUpdated={true}>
      <Button
        startIcon={<BackIcon />}
        onClick={handleBack}
        sx={{ py: 2, mt: -4 }}
      >
        Back to Homepage
      </Button>

      <article>
        {/* Article Header */}
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h3"
            component="h1"
            gutterBottom
            sx={{ fontWeight: "bold", lineHeight: 1.2, fontSize: "1.8rem" }}
          >
            {article.title}
          </Typography>

          <Typography
            variant="h6"
            color="text.secondary"
            paragraph
            sx={{ fontStyle: "italic" }}
          >
            {article.subtitle}
          </Typography>

          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 2 }}>
            <Typography variant="caption" color="text.secondary">
              Published: {formatDate(article.publishedAt)}
            </Typography>
            {article.category && (
              <Chip label={article.category} size="small" color="primary" />
            )}
          </Box>
        </Box>

        <Divider sx={{ mb: 4 }} />

        {/* Video on top if present, otherwise show images */}
        {article.videoLinks && article.videoLinks.length > 0 ? (
          <Box sx={{ mb: 4 }}>
            {article.videoLinks.map((link, index) => (
              <VideoEmbed key={index} url={link} />
            ))}
          </Box>
        ) : (
          article.images && article.images.length > 0 && (
            <Box sx={{ mb: 4 }}>
              {article.images.map((imageUrl, index) => (
                <Box
                  key={index}
                  component="img"
                  src={imageUrl}
                  alt={`${article.title} - Image ${index + 1}`}
                  sx={{
                    width: "100%",
                    maxHeight: 500,
                    objectFit: "contain",
                    borderRadius: 2,
                    mb: index < article.images!.length - 1 ? 3 : 0,
                  }}
                />
              ))}
            </Box>
          )
        )}

        {/* Article Content with images after first paragraph if video is present */}
        {(() => {
          const paragraphs = article.content.split(/\n\n+/);
          const hasVideo = article.videoLinks && article.videoLinks.length > 0;
          const hasImages = article.images && article.images.length > 0;

          if (hasVideo && hasImages && paragraphs.length > 1) {
            // Show first paragraph, then images, then rest
            return (
              <>
                <Typography
                  variant="body1"
                  component="div"
                  sx={{
                    whiteSpace: "pre-wrap",
                    lineHeight: 1.8,
                    fontSize: "1.16rem",
                    mb: 4,
                  }}
                >
                  {paragraphs[0]}
                </Typography>

                {/* Images after first paragraph */}
                <Box sx={{ mb: 4 }}>
                  {article.images!.map((imageUrl, index) => (
                    <Box
                      key={index}
                      component="img"
                      src={imageUrl}
                      alt={`${article.title} - Image ${index + 1}`}
                      sx={{
                        width: "100%",
                        maxHeight: 500,
                        objectFit: "contain",
                        borderRadius: 2,
                        mb: index < article.images!.length - 1 ? 3 : 0,
                      }}
                    />
                  ))}
                </Box>

                {/* Rest of content */}
                <Typography
                  variant="body1"
                  component="div"
                  sx={{
                    whiteSpace: "pre-wrap",
                    lineHeight: 1.8,
                    fontSize: "1.16rem",
                    mb: 0,
                  }}
                >
                  {paragraphs.slice(1).join('\n\n')}
                </Typography>
              </>
            );
          }

          // Default: show all content
          return (
            <Typography
              variant="body1"
              component="div"
              sx={{
                whiteSpace: "pre-wrap",
                lineHeight: 1.8,
                fontSize: "1.16rem",
                mb: 0,
              }}
            >
              {article.content}
            </Typography>
          );
        })()}
      </article>

      {/* Decorative End of Content Marker */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          my: 1,
          gap: 0.5,
        }}
      >
        {decorativeBullets.map((bullet, index) => (
          <Typography
            key={index}
            component="span"
            sx={{
              fontSize: "1.275rem",
              color: bullet.color,
              userSelect: "none",
              display: "inline-block",
              transition: "transform 0.3s ease",
              "&:hover": {
                transform: "scale(1.2)",
              },
            }}
          >
            {bullet.symbol}
          </Typography>
        ))}
      </Box>

      {/* Bottom Back to Home button */}
      <Box sx={{ mt: 0, display: "flex", justifyContent: "flex-start" }}>
        <Button
          startIcon={<BackIcon />}
          onClick={handleBack}
          sx={{ py: 2 }}
        >
          Back to Homepage
        </Button>
      </Box>
    </PublicLayout>
  );
};

export default ArticleDetail;
